import {
  create,
} from 'zustand';
import {
  RoomStatusUpdatedNotificationDTO,
} from 'dtos';
import {
  getApiRoomGetRoomStatus,
  getApiSettingsGetAppointmentSettings,
} from 'endpoints';
import {
  AppointmentStatusByRoomId,
  AppointmentStatusFilterOption,
  AppointmentStatusItem,
  applyRoomOccupancyUpdate,
  normalizeAppointmentStatus,
} from 'views/EMR/stores/appointmentStatusHelpers';

type FetchAppointmentStatusesOptions = {
  includeRoomStatuses?: boolean;
};

type Props = {
  statuses: AppointmentStatusItem[];
  byRoomId: AppointmentStatusByRoomId;
  values: string[];
  filterOptions: AppointmentStatusFilterOption[];
  encounterTypeStatuses: AppointmentStatusItem[];
  isLoading: boolean;
  actions: {
    fetchAppointmentStatuses: (options?: FetchAppointmentStatusesOptions) => Promise<void>;
    applyRoomStatusUpdate: (
      notification: RoomStatusUpdatedNotificationDTO,
    ) => void;
  };
};

const useAppointmentStatusStore = create<Props>((set, get) => ({
  statuses: [],
  byRoomId: {},
  values: [],
  filterOptions: [],
  encounterTypeStatuses: [],
  isLoading: true,
  actions: {
    fetchAppointmentStatuses: async ({
      includeRoomStatuses = true,
    }: FetchAppointmentStatusesOptions = {}) => {
      try {
        const appointmentSettings = await getApiSettingsGetAppointmentSettings();
        const roomStatuses = includeRoomStatuses
          ? await getApiRoomGetRoomStatus().catch(() => [])
          : [];
        const statusSettings = appointmentSettings?.patientAppointmentStatusSettings ?? [];

        set({
          ...normalizeAppointmentStatus(
            statusSettings,
            roomStatuses,
          ),
          isLoading: false,
        });
      } catch {
        set({ isLoading: false });
      }
    },
    applyRoomStatusUpdate: (notification) => {
      const {
        statuses,
        byRoomId,
        values,
        filterOptions,
        encounterTypeStatuses,
      } = get();
      set(applyRoomOccupancyUpdate(
        {
          statuses,
          byRoomId,
          values,
          filterOptions,
          encounterTypeStatuses,
        },
        notification,
      ));
    },
  },
}));

export function useAppointmentStatusActions() {
  return useAppointmentStatusStore((state: Props) => state.actions);
}

export function useAppointmentStatusItems(): AppointmentStatusItem[] {
  return useAppointmentStatusStore((state: Props) => state.statuses);
}

export function useAppointmentStatusOptions(): AppointmentStatusFilterOption[] {
  return useAppointmentStatusStore((state: Props) => state.filterOptions);
}

export function useEncounterTypeStatuses(): AppointmentStatusItem[] {
  return useAppointmentStatusStore((state: Props) => state.encounterTypeStatuses);
}

export function useAppointmentStatusLoading(): boolean {
  return useAppointmentStatusStore((state: Props) => state.isLoading);
}

export function useIsRoomOccupiedByName(name: string): boolean {
  return useAppointmentStatusStore((state: Props) => {
    const status = state.statuses.find((item) => item.value === name);
    return (status?.occupiedCount ?? 0) > 0;
  });
}
